import {
  Box,
  Tooltip,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import React from 'react';
import { colors as palette } from '../../../styleSheet';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';

// Cohesive emerald ramp (dark → light) so the stacked process segments read as one
// designed bar instead of a green→yellow "traffic light". Mirrors the Zippedi success hue.
const defaultColors = [
  palette.emerald900,
  palette.emerald800,
  palette.emerald700,
  palette.emerald,
  palette.emerald400,
  palette.teal300,
  palette.emerald300,
  palette.green300,
  palette.emerald200,
  palette.green200,
];

const MultiProgressBar = ({
  colors = defaultColors,
  progresses,
  progressNames,
  height,
  reportBlobPath,
  handleGoReport,
  navigationReportSentTimestamp,
  isLoadingSignedBlob = false,
  maxValue = 100,
}) => {
  const Parentdiv = {
    height: height,
    width: '100%',
    backgroundColor: palette.surfaceMutedBlue,
    display: 'flex',
    alignItems: 'stretch',
    borderRadius: 999,
    overflow: 'hidden',
    boxShadow: 'inset 0 0 0 1px rgba(26,36,45,0.05)',
    margin: '5px 0px',
  };

  const Childdiv = {
    height: '100%',
    textAlign: 'center',
  };

  const getColor = (index) => {
    const colorIndex = Math.floor((index * colors.length) / progresses.length);
    return colors[colorIndex];
  };

  const getProgressWidth = (index) => {
    if (index === progresses.length - 1) {
      return (Math.min(progresses[index], maxValue) * 100) / maxValue;
    }
    const value1 = Math.min(progresses[index], maxValue);
    const value2 = Math.min(progresses[index + 1], maxValue);
    return ((value1 - value2) * 100) / maxValue;
  };

  return (
    <>
      <Tooltip
        arrow
        placement="top"
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: palette.primary,
              color: palette.white,
              borderRadius: 2,
              px: 1.5,
              py: 1.25,
              maxWidth: 260,
              boxShadow: '0 8px 24px rgba(26,36,45,0.30)',
              '& .MuiTooltip-arrow': { color: palette.primary },
            },
          },
        }}
        title={
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.6)',
                mb: 0.5,
              }}
            >
              Planned Aisles: {maxValue}
            </Typography>
            {progressNames.map((progressName, index) => {
              return (
                <Box
                  key={`${progressName}-tooltip`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 0.25,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.85)' }}
                  >
                    {progressName}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                    {progresses[index]}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        }
      >
        <div style={Parentdiv}>
          {progresses
            .slice()
            .reverse()
            .map((progress, index) => {
              //slice so the array doesn't mutate
              return (
                <div
                  key={index}
                  style={{
                    ...Childdiv,
                    width: `${getProgressWidth(
                      progresses.length - 1 - index
                    )}%`,
                    backgroundColor: getColor(index),
                  }}
                />
              );
            })}
        </div>
      </Tooltip>
      {!isLoadingSignedBlob &&
      navigationReportSentTimestamp &&
      navigationReportSentTimestamp !== '-' ? (
        <Tooltip
          title={
            <Typography>
              Navigation Report Sent: {navigationReportSentTimestamp}
            </Typography>
          }
        >
          <IconButton
            sx={{
              pr: '4px',
              pt: '4px',
              pb: '0px',
              pl: '4px',
              maxHeight: '32px',
              mt: '2px',
            }}
            onClick={handleGoReport}
          >
            <Typography
              sx={{ alignSelf: 'center', color: 'rgba(0, 0, 0, 0.54)' }}
            >
              <MarkEmailReadOutlinedIcon />
            </Typography>
          </IconButton>
        </Tooltip>
      ) : isLoadingSignedBlob && navigationReportSentTimestamp ? (
        <Typography sx={{ alignSelf: 'center', color: 'rgba(0, 0, 0, 0.54)' }}>
          <CircularProgress sx={{ color: palette.slateMuted, ml: 1 }} size={20} />
        </Typography>
      ) : null}
    </>
  );
};

export default MultiProgressBar;
